import React from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Divider,
  Paper,
  IconButton,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Map as MapIcon,
  Add as AddIcon,
  Bookmark as BookmarkIcon,
  LocationOn as LocationOnIcon,
  ExpandLess,
  ExpandMore,
  AdsClick as AdsClickIcon,
} from "@mui/icons-material";
import PinDropIcon from "@mui/icons-material/PinDrop";

/** assetSearch tool drawer panel */
export default function AssetSearchToolPanel(ctx) {
  const {
    assetSearchMode,
    setAssetSearchMode,
    incidentPoint,
    searchRange,
    setSearchRange,
    searchUnit,
    setSearchUnit,
    assetLayerFilter,
    setAssetLayerFilter,
    assetResults,
    assetResultsExpanded,
    setAssetResultsExpanded,
    assetSearchLoading,
    layerGroups,
    isAssetSelectingRef,
    performAssetSearch,
    zoomToAssetFeature,
    clearAssetSearch,
    onCloseTool,
  } = ctx;

  return (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}
                  >
                    Asset Search (Nearby)
                  </Typography>

                  {/* Point Select */}
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}
                  >
                    Select Incident Point on Map
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2,
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={() => {
                        isAssetSelectingRef.current = true;
                        setAssetSearchMode("selecting");
                      }}
                      sx={{
                        bgcolor: "#1976d2",
                        color: "white",
                        borderRadius: 1,
                        width: 40,
                        height: 40,
                        border:
                          assetSearchMode === "selecting"
                            ? "2px solid #000"
                            : "2px solid transparent",
                        "&:hover": { bgcolor: "#2d9905" },
                      }}
                      title="Click a Point on Map for select"
                    >
                      <LocationOnIcon fontSize="small" />
                    </IconButton>
                    {/*  */}
                    {incidentPoint && (
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 11, color: "#4caf50", fontWeight: 600 }}
                      >
                        ✓ Point selected
                      </Typography>
                    )}
                  </Box>

                  {assetSearchMode === "selecting" && (
                    <Box
                      sx={{
                        mb: 2,
                        p: 1,
                        bgcolor: "#fff3e0",
                        borderRadius: 1,
                        border: "1px solid #1976d2",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontSize: 11, color: "#e65100" }}
                      >
                        🖱️ Click on the map to select a point
                      </Typography>
                    </Box>
                  )}

                  {/* Layer Filter */}
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}
                  >
                    Layer Filter
                  </Typography>
                  <Select
                    fullWidth
                    size="small"
                    value={assetLayerFilter}
                    onChange={(e) => setAssetLayerFilter(e.target.value)}
                    sx={{ mb: 2, bgcolor: "white" }}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {Object.entries(layerGroups).map(([groupKey, group]) =>
                      Object.entries(group.children).map(
                        ([layerKey, layer]) =>
                          !layer.children && (
                            <MenuItem key={layerKey} value={layerKey}>
                              {layer.name}
                            </MenuItem>
                          ),
                      ),
                    )}
                  </Select>

                  {/* Search Range */}
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5, fontWeight: 500, fontSize: 12 }}
                  >
                    Search Range
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={searchRange}
                      onChange={(e) =>
                        setSearchRange(Math.max(1, Number(e.target.value)))
                      }
                      sx={{ flex: 1, bgcolor: "white" }}
                      inputProps={{ min: 1 }}
                    />
                    <Select
                      size="small"
                      value={searchUnit}
                      onChange={(e) => setSearchUnit(e.target.value)}
                      sx={{ width: 120, bgcolor: "white" }}
                    >
                      <MenuItem value="meters">Meters</MenuItem>
                      <MenuItem value="kilometers">Kilometers</MenuItem>
                    </Select>
                  </Box>

                  <Divider sx={{ mb: 1 }} />

                  {/* Results */}
                  <Box sx={{ flex: 1, overflow: "auto" }}>
                    {assetSearchLoading ? (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          pt: 3,
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : Object.keys(assetResults).length === 0 ? (
                      <Typography
                        variant="caption"
                        sx={{ color: "#999", fontSize: 11 }}
                      >
                        {incidentPoint
                          ? "Search for results "
                          : "select  point "}
                      </Typography>
                    ) : (
                      Object.entries(assetResults).map(
                        ([layerKey, { layerName, features }]) => (
                          <Box key={layerKey} sx={{ mb: 1 }}>
                            {/* Layer Header */}
                            <Box
                              onClick={() =>
                                setAssetResultsExpanded((prev) => ({
                                  ...prev,
                                  [layerKey]: !prev[layerKey],
                                }))
                              }
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor: "pointer",
                                py: 0.8,
                                px: 1,
                                bgcolor: "#f0f4fa",
                                borderRadius: 1,
                                "&:hover": { bgcolor: "#e3f2fd" },
                              }}
                            >
                              {assetResultsExpanded[layerKey] ? (
                                <ExpandLess
                                  sx={{ fontSize: 18, color: "#1976d2" }}
                                />
                              ) : (
                                <ExpandMore
                                  sx={{ fontSize: 18, color: "#1976d2" }}
                                />
                              )}
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: "#1976d2",
                                  flex: 1,
                                  fontSize: 13,
                                }}
                              >
                                {layerName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: 10,
                                  bgcolor: "#1976d2",
                                  color: "white",
                                  px: 0.7,
                                  py: 0.2,
                                  borderRadius: 1,
                                }}
                              >
                                {features.length}
                              </Typography>
                            </Box>

                            {/* Features */}
                            <Collapse in={assetResultsExpanded[layerKey]}>
                              {features.map((feature, idx) => {
                                const props = feature.properties || {};
                                const displayName =
                                  props.name ||
                                  props.NAME ||
                                  props.id ||
                                  props.ID ||
                                  props.gid ||
                                  `Feature ${idx + 1}`;
                                const subText = Object.entries(props)
                                  .filter(
                                    ([k, v]) =>
                                      v !== null && v !== undefined && v !== "",
                                  )
                                  .slice(0, 2)
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(" | ");
                                return (
                                  <Box
                                    key={idx}
                                    onClick={() => zoomToAssetFeature(feature)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      px: 1.5,
                                      py: 0.8,
                                      ml: 1,
                                      borderLeft: "2px solid #e0e0e0",
                                      cursor: "pointer",
                                      "&:hover": {
                                        bgcolor: "#e3f2fd",
                                        borderLeftColor: "#1976d2",
                                      },
                                      transition: "all 0.15s",
                                    }}
                                  >
                                    <LocationOnIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "#1976d2",
                                        mr: 1,
                                        flexShrink: 0,
                                      }}
                                    />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: 500,
                                          color: "#333",
                                        }}
                                        noWrap
                                      >
                                        {String(displayName)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ fontSize: 10, color: "#888" }}
                                        noWrap
                                      >
                                        {subText}
                                      </Typography>
                                    </Box>
                                    <SearchIcon
                                      sx={{
                                        fontSize: 14,
                                        color: "#1976d2",
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Box>
                                );
                              })}
                            </Collapse>
                          </Box>
                        ),
                      )
                    )}
                  </Box>

                  <Divider sx={{ mt: 1, mb: 1 }} />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      onClick={performAssetSearch}
                      disabled={!incidentPoint || assetSearchLoading}
                      sx={{
                        bgcolor: "#1976d2",
                        "&:hover": { bgcolor: "#115293" },
                      }}
                    >
                      {assetSearchLoading ? (
                        <CircularProgress size={18} sx={{ color: "white" }} />
                      ) : (
                        "Search"
                      )}
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={clearAssetSearch}
                      sx={{ borderColor: "#1976d2", color: "#1976d2" }}
                    >
                      Clear
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        clearAssetSearch();
                        onCloseTool("assetSearch");
                      }}
                      sx={{
                        borderColor: "#f44336",
                        color: "#f44336",
                        "&:hover": { bgcolor: "#ffebee" },
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                </Box>
  );
}
